/**
 * STB cart — gift sync, one-tap upsell adds, milestone congratulations.
 *
 * Gift rules are evaluated in Liquid (snippets/stb-gift-plan.liquid, fed by the
 * stb_cart_gift metaobjects). The basket summary renders that plan straight away
 * (gift chip, savings) and exposes it as data-stb-gift-state. After every cart change
 * this module reads that state from the section HTML the change already returned and
 * fixes the gift lines with one /cart/update.js call in the background.
 *
 * 29.09. (speed audit): no extra round trip to /cart?view=stb-gifts any more when the
 * rendered state is available (that view is only the fallback), and checkout waits for a
 * running gift fix so an order can never leave without its free gifts.
 *
 * 21.10.2026: taps on remove / +/- while a cart update is running are held back and
 * replayed on the same line (by line key) once the cart is idle — see the end of the file.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const GIFT_VIEW = `${window.Theme?.routes?.cart_url || '/cart'}?view=stb-gifts`;
const OWN_SOURCE = 'stb-cart';
const STORE_KEY = 'stb_cart_duvets';

let syncing = false;
let queued = false;
/** @type {Record<string, string> | null} sections HTML handed over with a queued sync */
let queuedSections = null;
/** @type {number | null} milestone threshold that was just crossed */
let flashThreshold = null;

/** Pending cart work (user change + gift fix). Checkout waits for this. */
let pendingCount = 0;
/** @type {Array<() => void>} */
let idleResolvers = [];
const beginPending = () => {
  pendingCount += 1;
  document.documentElement.dataset.stbCartBusy = 'true';
};
const endPending = () => {
  pendingCount = Math.max(0, pendingCount - 1);
  if (pendingCount === 0) {
    delete document.documentElement.dataset.stbCartBusy;
    const resolvers = idleResolvers;
    idleResolvers = [];
    resolvers.forEach((resolve) => resolve());
  }
};
/** @param {number} timeoutMs */
const whenIdle = (timeoutMs) =>
  pendingCount === 0
    ? Promise.resolve()
    : Promise.race([
        new Promise((resolve) => idleResolvers.push(() => resolve(undefined))),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]);

const readPrev = () => {
  try {
    const v = sessionStorage.getItem(STORE_KEY);
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
};
const writePrev = (n) => {
  try {
    sessionStorage.setItem(STORE_KEY, String(n));
  } catch {
    /* storage unavailable */
  }
};

const parsePairs = (str) => {
  /** @type {Record<string, number>} */
  const map = {};
  String(str || '')
    .split(',')
    .filter(Boolean)
    .forEach((pair) => {
      const [id, qty] = pair.split(':');
      if (!id) return;
      map[id] = (map[id] || 0) + Number(qty || 0);
    });
  return map;
};

const decodeEntities = (s) => {
  const t = document.createElement('textarea');
  t.innerHTML = s;
  return t.value;
};

/**
 * Reads the gift state ("D=…;W=…;C=…;M=…;") that the basket summary rendered.
 * @param {Record<string, string> | null | undefined} sections
 */
function stateFromSections(sections) {
  if (!sections) return null;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string') continue;
    const match = html.match(/data-stb-gift-state="([^"]*)"/);
    if (!match) continue;
    /** @type {Record<string, string>} */
    const kv = {};
    decodeEntities(match[1])
      .split(';')
      .filter(Boolean)
      .forEach((part) => {
        const i = part.indexOf('=');
        if (i > 0) kv[part.slice(0, i)] = part.slice(i + 1);
      });
    if (!('W' in kv) || !('C' in kv)) continue;
    return { duvets: Number(kv.D || 0), desired: kv.W, current: kv.C, milestones: kv.M || '' };
  }
  return null;
}

const sectionIds = () =>
  Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

/** Paid items only — free gift lines (price 0 / SKU …-GIFT) don't count as items. */
export const paidItemCount = (items) =>
  (Array.isArray(items) ? items : []).reduce((sum, item) => {
    const isGift = Number(item.original_price) === 0 || /-GIFT$/i.test(String(item.sku || ''));
    return isGift ? sum : sum + Number(item.quantity || 0);
  }, 0);

/**
 * Posts a cart mutation and lets Horizon's cart components morph from the returned sections.
 * @param {string} url
 * @param {object} payload
 * @param {'add' | 'update'} action
 */
async function mutateCart(url, payload, action, lines = []) {
  const ids = sectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines,
      promise: deferred.promise,
    })
  );
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: ids.join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (data.status) throw new Error(data.description || data.message || 'Cart update failed');
    const cart = data.token ? data : await (await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`)).json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: paidItemCount(cart.items),
        source: OWN_SOURCE,
        sections: data.sections,
        didError: false,
      },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

/**
 * @param {Record<string, string> | null} [sections] section HTML returned by the change that triggered this
 */
async function syncGifts(sections = null) {
  if (syncing) {
    queued = true;
    queuedSections = sections || null;
    return;
  }
  syncing = true;
  beginPending();
  try {
    let state = stateFromSections(sections);
    if (!state) {
      // Fallback (first load, or a change that returned no section HTML).
      // NOTE: no `Accept: application/json` header here — with it Shopify ignores ?view=.
      const res = await fetch(GIFT_VIEW, { cache: 'no-store' });
      state = JSON.parse(await res.text());
    }
    const desired = parsePairs(state.desired);
    const current = parsePairs(state.current);

    /** @type {Record<string, number>} */
    const updates = {};
    for (const [id, qty] of Object.entries(desired)) {
      if (current[id] !== qty) updates[id] = qty;
    }
    for (const id of Object.keys(current)) {
      if (!(id in desired)) updates[id] = 0;
    }

    // Congratulation when a milestone was crossed upwards.
    const prev = readPrev();
    const now = Number(state.duvets || 0);
    flashThreshold = null;
    if (prev !== null && now > prev) {
      const crossed = String(state.milestones || '')
        .split(',')
        .filter(Boolean)
        .map(Number)
        .filter((t) => t > prev && t <= now);
      if (crossed.length) flashThreshold = Math.max(...crossed);
    }
    writePrev(now);

    if (Object.keys(updates).length) {
      const lines = Object.entries(updates).map(([id, quantity]) => ({ merchandiseId: id, quantity }));
      await mutateCart(`${window.Theme?.routes?.cart_update_url || '/cart/update.js'}`, { updates }, 'update', lines);
    }
  } catch (error) {
    console.warn('[stb-cart] gift sync failed', error);
  } finally {
    syncing = false;
    requestAnimationFrame(applyFlash);
    if (queued) {
      // Changes overlapped: the queued section HTML may predate our own gift fix,
      // so re-read the fresh state from the gift view instead.
      queued = false;
      queuedSections = null;
      syncGifts(null);
    }
    endPending();
  }
}

function applyFlash() {
  document.querySelectorAll('[data-stb-flash]').forEach((el) => {
    if (!(el instanceof HTMLElement)) return;
    el.hidden = flashThreshold === null || Number(el.dataset.stbFlash) !== flashThreshold;
  });
}

// Every cart change (product form, drawer quantity, our own upsell adds) → re-check gifts.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const e = /** @type {any} */ (event);
  if (!e.promise) return;
  beginPending();
  e.promise
    .then((result) => {
      if (result?.detail?.source === OWN_SOURCE && e.action === 'update') return; // our own gift fix
      return syncGifts(result?.detail?.sections || null);
    })
    .catch(() => {})
    .finally(endPending);
});

// Checkout waits (max. 6 s) until a running quantity change + gift fix is done,
// so the order always contains the free gifts that belong to it.
document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[name="checkout"]') : null;
    if (!(button instanceof HTMLButtonElement) || pendingCount === 0) return;
    const form = button.form;
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    button.setAttribute('aria-busy', 'true');
    whenIdle(6000).then(() => {
      button.removeAttribute('aria-busy');
      form.requestSubmit ? form.requestSubmit(button) : form.submit();
    });
  },
  true
);

// One-tap upsell buttons in the drawer.
document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target.closest('[data-stb-add]') : null;
  if (!(target instanceof HTMLButtonElement) || target.disabled) return;
  event.preventDefault();
  const id = Number(target.dataset.stbAdd);
  const quantity = Number(target.dataset.stbQty || 1);
  target.disabled = true;
  target.setAttribute('aria-busy', 'true');
  try {
    await mutateCart(window.Theme?.routes?.cart_add_url || '/cart/add.js', { items: [{ id, quantity }] }, 'add', [
      { merchandiseId: String(id), quantity },
    ]);
  } catch (error) {
    console.warn('[stb-cart] add failed', error);
    target.disabled = false;
    target.removeAttribute('aria-busy');
  }
});

// Size picker for offers where the bed size can differ (fitted sheet, duvet for accessory carts).
document.addEventListener('click', (event) => {
  const t = event.target instanceof Element ? event.target : null;
  if (!t) return;
  const open = t.closest('[data-stb-pick]');
  if (open) {
    const panel = open.closest('.stb-xs, .stb-upg')?.querySelector('[data-stb-picker]');
    if (!(panel instanceof HTMLElement)) return;
    panel.hidden = !panel.hidden;
    open.setAttribute('aria-expanded', String(!panel.hidden));
    if (!panel.hidden) {
      const current = panel.querySelector('[aria-checked="true"]');
      if (current instanceof HTMLElement) current.focus();
      panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    return;
  }
  const close = t.closest('[data-stb-pick-close]');
  if (close) {
    const panel = close.closest('[data-stb-picker]');
    if (panel instanceof HTMLElement) panel.hidden = true;
    const trigger = panel?.closest('.stb-xs, .stb-upg')?.querySelector('[data-stb-pick]');
    trigger?.setAttribute('aria-expanded', 'false');
    return;
  }
  // Colour choice (duvets in two or more colours in the basket): show that colour's sizes,
  // pre-select the size of the duvet in that colour, and show that colour's picture.
  const colour = t.closest('[data-stb-colour]');
  if (colour instanceof HTMLElement) {
    const panel = colour.closest('[data-stb-picker]');
    if (!panel) return;
    const name = colour.dataset.stbColour || '';
    panel.querySelectorAll('[data-stb-colour]').forEach((b) => b.setAttribute('aria-checked', String(b === colour)));
    let group = null;
    panel.querySelectorAll('[data-stb-group]').forEach((g) => {
      if (!(g instanceof HTMLElement)) return;
      const on = g.dataset.stbGroup === name;
      g.hidden = !on;
      if (on) group = g;
    });
    const first = group ? group.querySelector('[data-stb-opt][data-stb-default]') || group.querySelector('[data-stb-opt]') : null;
    if (first instanceof HTMLElement) selectOption(first);
    const img = panel.closest('.stb-xs, .stb-upg')?.querySelector('.stb-xs__media img, .stb-upg__media img');
    if (img instanceof HTMLImageElement && colour.dataset.stbImg) {
      img.removeAttribute('srcset');
      img.src = colour.dataset.stbImg;
    }
    return;
  }
  const opt = t.closest('[data-stb-opt]');
  if (opt instanceof HTMLElement) selectOption(opt);
});

/** @param {HTMLElement} opt */
function selectOption(opt) {
  const panel = opt.closest('[data-stb-picker]');
  if (!panel) return;
  panel.querySelectorAll('[data-stb-opt]').forEach((b) => b.setAttribute('aria-checked', String(b === opt)));
  const add = panel.querySelector('[data-stb-add]');
  if (add instanceof HTMLElement) add.dataset.stbAdd = opt.dataset.stbOpt || '';
  const price = panel.querySelector('[data-stb-pick-price]');
  if (price) price.textContent = opt.dataset.stbPrice || '';
  const sub = panel.closest('.stb-xs, .stb-upg')?.querySelector('[data-stb-sub-target]');
  if (sub && opt.dataset.stbSub) sub.textContent = opt.dataset.stbSub;
}

// 21.10.2026: line safety. Horizon's remove and +/- buttons address a cart line by its
// position (line index). While a cart update or gift fix is running, gift lines are added or
// removed and the positions shift, so a tap in that moment could change the wrong line (e.g. a
// free pillowcase instead of the duvet) and the duvet stayed in the basket. Such taps are held
// back and replayed on the same row (found by its line key) as soon as the cart is idle.
const LINE_CONTROL = '.cart-items__remove, cart-quantity-selector-component button';
document.addEventListener(
  'click',
  (event) => {
    if (pendingCount === 0) return;
    const control = event.target instanceof Element ? event.target.closest(LINE_CONTROL) : null;
    if (!(control instanceof HTMLElement)) return;
    const row = control.closest('.cart-items__table-row[data-key]');
    if (!(row instanceof HTMLElement) || !row.dataset.key) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const key = row.dataset.key;
    const kind = control.classList.contains('cart-items__remove') ? 'remove' : control.getAttribute('name') || '';
    whenIdle(6000).then(() => {
      const freshRow = Array.from(document.querySelectorAll('cart-items-component .cart-items__table-row[data-key]')).find(
        (r) => r instanceof HTMLElement && r.dataset.key === key
      );
      if (!freshRow) return;
      const target =
        kind === 'remove'
          ? freshRow.querySelector('.cart-items__remove')
          : freshRow.querySelector(`cart-quantity-selector-component button[name="${kind}"]`);
      if (target instanceof HTMLButtonElement && !target.disabled) target.click();
    });
  },
  true
);

// While the cart is busy the line controls are dimmed, so a tap is visibly "in progress".
(() => {
  const style = document.createElement('style');
  style.textContent =
    'html[data-stb-cart-busy] cart-items-component .cart-items__quantity{opacity:.5;transition:opacity .15s}';
  document.head.appendChild(style);
})();

// Horizon morphs the drawer after every change; keep the flash state applied.
new MutationObserver(() => {
  if (flashThreshold !== null) applyFlash();
}).observe(document.documentElement, { childList: true, subtree: true });

// Initial reconcile (e.g. a cart carried over from an earlier visit).
syncGifts();
